import { useMemo, useState } from 'react';
import {
  Box, CircularProgress, Stack, Button, Dialog, DialogContent, DialogActions,
  TextField, FormControl, InputLabel, Select, MenuItem,
} from '@mui/material';
import EventIcon from '@mui/icons-material/Event';
import FlagIcon from '@mui/icons-material/Flag';
import { useTranslation } from 'react-i18next';
import { useArbeitspakete, useUpdateArbeitspaket } from '../../../api/hooks/useArbeitspakete';
import { useSyncMaturityFromCount } from '../../../api/hooks/useReifegrad';
import { useProjectMilestones, useCreateMilestone } from '../../../api/hooks/useProjects';
import { useProjectInitialization } from '../../../api/hooks/useProjectInitialization';
import { MilestoneFormDialog, teilzielVorschlaege } from './MilestoneFormDialog';
import { useMeetings, useCreateMeeting } from '../../../api/hooks/useMeetings';
import { useOffenePunkte } from '../../../api/hooks/useOffenePunkte';
import { useNotifications } from '../../../context/NotificationContext';
import { formatApiError } from '../../../api/client';
import { combineDateTimeToIso } from '../lib/dateTimeIso';
import { MEETING_TYPES } from './MeetingDialog';
import {
  PmProjectGantt,
  type GanttArbeitspaket, type GanttMilestone, type GanttMeeting, type GanttOffenerPunkt,
} from './PmProjectGantt';
import { apiErrorMessage } from '../../../lib/apiErrorMessage';
import { AppDialogTitle } from '../../../components/common/AppDialogTitle';

/**
 * Zeitliche Darstellung der Arbeitspakete (Gantt) inkl. Meilensteine, Meetings
 * und offener Punkte. Reine Darstellung — der aufrufende Tab entscheidet ueber
 * Ueberschrift und Reifegrad-Semantik (via maturityStep; nur der Projektplan-Tab
 * synct hier Schritt 11, der Arbeitspakete-Tab synct den Listen-Schritt separat).
 */
export function ProjectGanttSection({
  projectId, maturityStep,
}: {
  projectId: string;
  maturityStep?: number;
}) {
  const { t } = useTranslation('projects');
  const { t: tc } = useTranslation('common');
  const { notify } = useNotifications();
  const { data: apResp, isLoading: apLoading } = useArbeitspakete(projectId);
  const { data: msResp } = useProjectMilestones(projectId);
  const { data: mtResp } = useMeetings(projectId);
  const { data: opResp } = useOffenePunkte(projectId);
  const updateAp = useUpdateArbeitspaket(projectId);
  const createMeeting = useCreateMeeting(projectId);
  const createMilestone = useCreateMilestone();
  const { data: initData } = useProjectInitialization(projectId);
  const [msEditId, setMsEditId] = useState<string | null>(null);

  // Projektweiter Termin (ohne ap_id) — direkt im Projektplan-Gesamt-Gantt anlegbar.
  const [termOpen, setTermOpen] = useState(false);
  const [termForm, setTermForm] = useState({ titel: '', date: '', time: '', typ: 'Team Meeting' });
  const handleCreateTermin = async () => {
    if (termForm.date.trim().length === 0) return;
    try {
      await createMeeting.mutateAsync({
        titel: termForm.titel.trim() || null,
        typ: termForm.typ,
        datum: combineDateTimeToIso(termForm.date, termForm.time),
        ap_id: null,
      });
      notify(tc('status.saved', 'Gespeichert'), 'success');
      setTermOpen(false);
      setTermForm({ titel: '', date: '', time: '', typ: 'Team Meeting' });
    } catch (err) { notify(formatApiError(err), 'error'); }
  };

  const rawMilestones = useMemo(() => (msResp as unknown as { data?: Array<Record<string, unknown> & { id: string }> } | undefined)?.data ?? [], [msResp]);

  // PHQ-101: fehlende Meilensteine aus den Teilzielen des Projektauftrags anlegen (ohne Datum).
  const fehlendeTeilziele = useMemo(
    () => teilzielVorschlaege(initData.ziele_teil ?? '', rawMilestones.map((m) => String(m.name ?? m.titel ?? ''))),
    [initData.ziele_teil, rawMilestones],
  );
  const handleCreateFromTeilziele = async () => {
    try {
      for (const name of fehlendeTeilziele) {
        await createMilestone.mutateAsync({ projectId, data: { name, deadline: null, rank: 'NORMAL', state: 'CREATED' } });
      }
      notify(tc('status.saved', 'Gespeichert'), 'success');
    } catch (err) { notify(formatApiError(err), 'error'); }
  };

  const aps: GanttArbeitspaket[] = useMemo(
    () => ((apResp as unknown as { data?: GanttArbeitspaket[] } | undefined)?.data ?? []),
    [apResp],
  );

  const milestones: GanttMilestone[] = useMemo(() => {
    const raw = (msResp as unknown as { data?: Array<{ id: string; name?: string | null; titel?: string | null; date?: string | null; deadline?: string | null; faellig?: string | null }> } | undefined)?.data ?? [];
    return raw.map((m) => ({
      id: m.id,
      name: m.name ?? m.titel ?? '—',
      date: m.date ?? m.deadline ?? m.faellig ?? null,
    }));
  }, [msResp]);

  const meetings: GanttMeeting[] = useMemo(
    () => ((mtResp as unknown as { data?: GanttMeeting[] } | undefined)?.data ?? []),
    [mtResp],
  );

  const offenePunkte: GanttOffenerPunkt[] = useMemo(
    () => ((opResp as unknown as { data?: GanttOffenerPunkt[] } | undefined)?.data ?? []),
    [opResp],
  );

  // Reifegrad-Sync nur wenn der Tab einen Schritt zuweist (Projektplan = Schritt 11).
  // count < 0 ist der No-op-Sentinel im Hook.
  useSyncMaturityFromCount(
    projectId,
    maturityStep ?? 0,
    maturityStep == null ? -1 : (apLoading ? -1 : aps.length),
  );

  const handleApMove = async (id: string, newStart: string, newEnd: string) => {
    try {
      await updateAp.mutateAsync({ id, data: { start_date: newStart, end_date: newEnd } });
      notify(tc('status.saved', 'Gespeichert'), 'success');
    } catch (err) {
      notify(apiErrorMessage(err, tc('errors.generic', 'Fehler beim Verschieben')), 'error');
    }
  };

  if (apLoading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>;
  }

  return (
    <Box>
      <Stack direction="row" justifyContent="flex-end" spacing={1} sx={{ mb: 1 }}>
        {fehlendeTeilziele.length > 0 && (
          <Button size="small" variant="outlined" startIcon={<FlagIcon />} onClick={handleCreateFromTeilziele} disabled={createMilestone.isPending}>
            {t('milestones.fromTeilziele', 'Meilensteine aus Teilzielen anlegen')}
          </Button>
        )}
        <Button size="small" variant="outlined" startIcon={<EventIcon />} onClick={() => setTermOpen(true)}>
          {t('meetings.addProjectTermin', 'Termin hinzufügen')}
        </Button>
      </Stack>
      <PmProjectGantt
        arbeitspakete={aps}
        milestones={milestones}
        meetings={meetings}
        offenePunkte={offenePunkte}
        onApMove={handleApMove}
        onMilestoneClick={setMsEditId}
      />
      <MilestoneFormDialog
        open={!!msEditId}
        mode="edit"
        projectId={projectId}
        initialData={rawMilestones.find((m) => m.id === msEditId)}
        onClose={() => setMsEditId(null)}
      />

      <Dialog open={termOpen} onClose={() => setTermOpen(false)} maxWidth="xs" fullWidth>
        <AppDialogTitle>{t('meetings.addProjectTerminTitle', 'Projekt-Termin hinzufügen')}</AppDialogTitle>
        <DialogContent>
          <Stack spacing={2.5} sx={{ mt: 1 }}>
            <TextField
              label={t('meetings.titel', 'Titel')} value={termForm.titel} fullWidth
              onChange={(e) => setTermForm((f) => ({ ...f, titel: e.target.value }))}
            />
            <TextField
              type="date" label={t('meetings.date', 'Datum')} required fullWidth value={termForm.date}
              onChange={(e) => setTermForm((f) => ({ ...f, date: e.target.value }))}
              slotProps={{ inputLabel: { shrink: true } }}
            />
            <TextField
              type="time" label={t('meetings.time', 'Uhrzeit')} fullWidth value={termForm.time}
              onChange={(e) => setTermForm((f) => ({ ...f, time: e.target.value }))}
              slotProps={{ inputLabel: { shrink: true } }}
            />
            <FormControl fullWidth>
              <InputLabel>{t('meetings.typ', 'Typ')}</InputLabel>
              <Select
                value={termForm.typ} label={t('meetings.typ', 'Typ')}
                onChange={(e) => setTermForm((f) => ({ ...f, typ: e.target.value }))}
              >
                {MEETING_TYPES.map((tp) => <MenuItem key={tp} value={tp}>{tp}</MenuItem>)}
              </Select>
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTermOpen(false)}>{tc('actions.cancel', 'Abbrechen')}</Button>
          <Button variant="contained" onClick={handleCreateTermin} disabled={termForm.date.trim().length === 0 || createMeeting.isPending}>
            {tc('actions.save', 'Speichern')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
